/**
 * App.js — Root component: route configuration and auth guards
 *
 * Tree structure:
 *   AuthProvider         — session state, login/logout, role flags
 *   └── AlertProvider    — global toast notification system
 *       └── BrowserRouter
 *           └── Routes
 *               ├── /login           → AuthPage       (PublicRoute)
 *               ├── /                → HomePage       (ProtectedRoute)
 *               ├── /new-worker      → NewWorkerPage  (ProtectedRoute)
 *               ├── /worker/:id      → ViewWorkerPage     (ProtectedRoute)
 *               ├── /workers         → ViewAllWorkersPage (ProtectedRoute)
 *               └── *                → redirect to /
 *
 * Both guard components render a "Loading…" spinner while AuthContext is
 * still validating the session on mount, preventing a flash of the wrong page.
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AlertProvider } from './components/UI';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import NewWorkerPage from './pages/NewWorkerPage';
import ViewWorkerPage from './pages/ViewWorkerPage';
import ViewAllWorkersPage from './pages/ViewAllWorkersPage';
import PayslipPage from './pages/PayslipPage';
import './index.css';

/**
 * Dismisses the PWA splash screen once the auth check has resolved.
 *
 * Enforces a 1-second minimum display time so the splash is always visible
 * on app launch — previously it was removed immediately in index.js, making
 * it appear only as a brief flash (or not at all on fast devices).
 *
 * Only active in standalone PWA mode; in the browser the splash is hidden
 * by CSS (display:none outside @media (display-mode: standalone)).
 */
function SplashController() {
  const { loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    const splash = document.getElementById('pwa-splash');
    if (!splash) return;

    const elapsed   = Date.now() - (window.__splashStart || Date.now());
    const remaining = Math.max(0, 1000 - elapsed);

    const timer = setTimeout(() => {
      splash.style.transition = 'opacity 0.3s ease';
      splash.style.opacity    = '0';
      setTimeout(() => splash.remove(), 300);
    }, remaining);

    return () => clearTimeout(timer);
  }, [loading]);

  return null;
}

/**
 * Blocks unauthenticated access. Redirects to /login if there is no active
 * session. Must be used with `replace` to avoid adding the guarded route to
 * browser history (so Back doesn't loop back to a page the user can't access).
 */
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return <div className="loader">Loading…</div>;
  if (!currentUser) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Prevents logged-in users from reaching the login page. Redirects to the
 * dashboard if a session already exists. Avoids the awkward UX of a logged-in
 * user being able to navigate back to /login.
 */
function PublicRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return <div className="loader">Loading…</div>;
  if (currentUser) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <SplashController />
      <AlertProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/new-worker" element={<ProtectedRoute><NewWorkerPage /></ProtectedRoute>} />
            <Route path="/worker/:id" element={<ProtectedRoute><ViewWorkerPage /></ProtectedRoute>} />
            <Route path="/worker/:id/payslip/:payslipId" element={<ProtectedRoute><PayslipPage /></ProtectedRoute>} />
            <Route path="/workers" element={<ProtectedRoute><ViewAllWorkersPage /></ProtectedRoute>} />
            {/* Catch-all: unknown paths fall back to the dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AlertProvider>
    </AuthProvider>
  );
}
